/**
 * DocumentsScreen.jsx
 * Mobile App version of the documents/insurance forms screen
 * Allows customers to view and download their health insurance forms
 */
import React, { useState, useEffect } from 'react';
import { 
  FileText, Download, Calendar, AlertCircle, Check, 
  Lock, Info, ChevronRight, RefreshCw, X, Shield
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const DocumentsScreen = ({ portalToken, customerId, onNavigate }) => {
  const { t } = useTranslation();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [insuranceInfo, setInsuranceInfo] = useState({
    insurance_name: '',
    insurance_number: '',
    supplementary_insurance_number: ''
  });
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (portalToken && customerId) {
      loadForms();
    }
  }, [portalToken, customerId]);

  const loadForms = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/insurance/portal/available-forms?customer_id=${customerId}`,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      setForms(response.data.forms || []);
    } catch (error) {
      console.error('Error loading forms:', error);
      // Silent fail - user might not have any forms
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDownload = (form) => {
    setSelectedForm(form);
    setShowDownloadModal(true);
  };

  const handleDownload = async () => {
    if (!insuranceInfo.insurance_name || !insuranceInfo.insurance_number) {
      toast.error(t('documents.fill_required'));
      return;
    }

    setDownloading(true);
    try {
      const response = await axios.post(
        `${API}/insurance/portal/generate-form?customer_id=${customerId}`,
        {
          insurance_name: insuranceInfo.insurance_name,
          insurance_number: insuranceInfo.insurance_number,
          supplementary_insurance_number: insuranceInfo.supplementary_insurance_number || null,
          year: selectedForm.year
        },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );

      if (response.data.success && response.data.pdf_base64) {
        // Convert base64 to blob and download
        const byteCharacters = atob(response.data.pdf_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename || `Abonnementbestätigung_${selectedForm.year}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success(t('documents.downloaded'));
        setShowDownloadModal(false);
        setInsuranceInfo({ insurance_name: '', insurance_number: '', supplementary_insurance_number: '' });
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.detail || t('documents.download_error'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center pt-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/30 to-indigo-500/20 flex items-center justify-center">
            <FileText className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h1 className="text-white text-base font-bold">{t('documents.title')}</h1>
            <p className="text-zinc-400 text-xs">{t('documents.subtitle')}</p>
          </div>
        </div>
        <button 
          onClick={loadForms}
          disabled={loading}
          className="p-2 bg-zinc-800 rounded-lg"
        >
          <RefreshCw className={`w-5 h-5 text-zinc-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="bg-zinc-800/50 rounded-2xl p-8 text-center">
          <RefreshCw className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">{t('documents.loading')}</p>
        </div>
      ) : forms.length === 0 ? (
        /* Empty State */
        <div className="bg-zinc-800/50 rounded-2xl p-6 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto">
            <FileText className="w-8 h-8 text-blue-400" />
          </div>
          <div>
            <p className="text-white font-medium">{t('documents.empty')}</p>
            <p className="text-zinc-400 text-sm mt-1">
              {t('documents.empty_hint')}
            </p>
          </div>
        </div>
      ) : (
        /* Forms List */
        <div className="space-y-3">
          {/* Info Box */}
          <div className="bg-blue-500/10 rounded-xl p-4 flex gap-3 border border-blue-500/20">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-zinc-300 text-xs">
              {t('documents.info_text')}
            </p>
          </div>

          {/* Forms */}
          {forms.map((form, idx) => (
            <button
              key={idx}
              onClick={() => handleOpenDownload(form)}
              className="w-full bg-zinc-800/50 rounded-xl p-4 flex items-center justify-between hover:bg-zinc-800 transition-colors"
              data-testid={`form-${form.year}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-green-400" />
                </div>
                <div className="text-left">
                  <p className="text-white font-medium text-sm">
                    {t('documents.certificate', { year: form.year })}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Calendar className="w-3 h-3 text-zinc-500" />
                    <span className="text-zinc-500 text-xs">
                      {t('documents.trainings_count', { count: form.training_count || 0 })}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Download className="w-5 h-5 text-yellow-400" />
                <ChevronRight className="w-4 h-4 text-zinc-500" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Download Modal */}
      {showDownloadModal && selectedForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end">
          <div className="w-full bg-zinc-900 rounded-t-3xl p-5 space-y-4 animate-slide-up">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-400" />
                {t('documents.insurance_data')}
              </h3>
              <button 
                onClick={() => setShowDownloadModal(false)}
                className="text-zinc-400 text-lg p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-zinc-400 text-sm">
              {t('documents.insurance_desc', { year: selectedForm.year })}
            </p>

            <div className="space-y-3">
              <div>
                <label className="text-zinc-400 text-sm block mb-1">
                  {t('documents.insurance_name')} *
                </label>
                <input
                  type="text"
                  value={insuranceInfo.insurance_name}
                  onChange={(e) => setInsuranceInfo({...insuranceInfo, insurance_name: e.target.value})}
                  placeholder={t('documents.insurance_placeholder')}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500"
                />
              </div>
              
              <div>
                <label className="text-zinc-400 text-sm block mb-1">
                  {t('documents.insurance_number')} *
                </label>
                <input
                  type="text"
                  value={insuranceInfo.insurance_number}
                  onChange={(e) => setInsuranceInfo({...insuranceInfo, insurance_number: e.target.value})}
                  placeholder={t('documents.insurance_num_placeholder')}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500"
                />
              </div>
              
              <div>
                <label className="text-zinc-400 text-sm block mb-1">
                  {t('documents.supplementary')}
                </label>
                <input
                  type="text"
                  value={insuranceInfo.supplementary_insurance_number}
                  onChange={(e) => setInsuranceInfo({...insuranceInfo, supplementary_insurance_number: e.target.value})}
                  placeholder={t('documents.supplementary_placeholder')}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500"
                />
              </div>
            </div>

            {/* Privacy Note */}
            <div className="bg-zinc-800 rounded-lg p-3 flex gap-2">
              <Lock className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
              <p className="text-zinc-500 text-xs">
                {t('documents.privacy_note')}
              </p>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setShowDownloadModal(false)}
                className="flex-1 py-3 bg-zinc-800 text-zinc-300 rounded-xl font-medium"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDownload}
                disabled={downloading || !insuranceInfo.insurance_name || !insuranceInfo.insurance_number}
                className="flex-1 py-3 bg-yellow-500 text-black rounded-xl font-medium flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {downloading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    {t('documents.download')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsScreen;
